import React from "react";
import Image from "next/legacy/image";
import { Flex, Text, Box } from "@chakra-ui/react";
import { Link } from "ui/link";
import { trackEvent } from "lib/analytics";
import { Badge } from "ui/badge";

type PlaybookProps = {
  hit: any;
  onSendEvent: (eventType: string, hit: any, eventName?: string | undefined) => void;
};

export function Playbook(props: PlaybookProps) {
  const { hit } = props;
  return (
    <Link href={`/playbooks/${hit.slug}`} variant="unstyled" w="100%" aria-label={`${hit.title}.`}>
      <PlaybookCard {...props} />
    </Link>
  );
}

function PlaybookCard({ hit, onSendEvent }: PlaybookProps) {
  return (
    <Flex
      position="relative"
      flexDirection="column"
      align="start"
      justify="start"
      gap="small"
      w="100%"
      h="100%"
      p={8}
      border="1px"
      borderColor="base.border"
      borderRadius="base"
      opacity={hit.status === "shadow" ? 0.5 : 1}
      transition="all .15s ease-in-out"
      role="group"
      _hover={{
        borderColor: "gray.400",
        opacity: 1,
      }}
      onClick={() => {
        onSendEvent("conversion", hit, `Playbook Conversion Clicked`);
        trackEvent("Playbook Clicked");
      }}
    >
      <Text textStyle="headline3">{hit.title}</Text>

      <Flex gap={2} wrap="wrap">
        <Badge bg="new.green.50">{hit.team.name}</Badge>
        <Badge bg="new.yellow.50">{hit.industry.name}</Badge>
        <Badge bg="new.blue.50">{hit.experienceLevel}</Badge>
      </Flex>

      {hit.authors?.length && (
        <Text color="brand.textSecondary.light" textStyle="caption2">
          Made by:
          {hit.authors?.map((author: string, index: number) => (
            <Text key={author} as="span" ml={1}>
              {author + (hit.authors.length > 1 && index < hit.authors.length - 1 ? ", " : " ")}
            </Text>
          ))}
        </Text>
      )}

      {hit.excerpt && (
        <Text color="brand.textSecondary.light" textStyle="caption1">
          {hit.excerpt}
        </Text>
      )}

      {(hit.integrations || hit.extensions) && (
        <Flex gap={6} mt="auto">
          {hit.integrations.map((integration: { name: string; slug: string; icon: { asset: { url: string } } }) => (
            <Box key={integration.slug} width="36px" height="36px" pos="relative" mt={10} fontSize="xs">
              <Image
                src={integration.icon.asset.url + "?w=72"}
                alt={`${integration.name}.`}
                layout="fill"
                objectFit="contain"
              />
            </Box>
          ))}

          {hit.extensions?.map((extension: { name: string; slug: string; icon: { asset: { url: string } } }) => (
            <Box key={extension.slug} width="36px" height="36px" pos="relative" mt={10} fontSize="xs">
              <Image
                src={extension.icon.asset.url + "?w=72"}
                alt={`${extension.name}.`}
                layout="fill"
                objectFit="contain"
              />
            </Box>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
